import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { TableOfContents, type TocItem } from './TableOfContents';
import { MobileTocSheet } from './components/MobileTocSheet';
import { GettingStartedSection } from './sections/GettingStartedSection';
import { DashboardSection } from './sections/DashboardSection';
import { TicketListSection } from './sections/TicketListSection';
import { CreatingTicketsSection } from './sections/CreatingTicketsSection';
import { TicketDetailSection } from './sections/TicketDetailSection';
import { AddressInformationSection } from './sections/AddressInformationSection';
import { ReportingSection } from './sections/ReportingSection';
import { NotificationsSection } from './sections/NotificationsSection';
import { ProfileSection } from './sections/ProfileSection';
import { TipsSection } from './sections/TipsSection';

const TOC_ITEMS: TocItem[] = [
  { id: 'getting-started', labelKey: 'sections.gettingStarted.title' },
  { id: 'dashboard', labelKey: 'sections.dashboard.title' },
  { id: 'ticket-list', labelKey: 'sections.ticketList.title' },
  {
    id: 'creating-tickets',
    labelKey: 'sections.creatingTickets.title',
    children: [
      { id: 'creating-tickets', labelKey: 'sections.creatingTickets.orderManagement.title' },
      { id: 'creating-tickets', labelKey: 'sections.creatingTickets.addressManagement.title' },
      { id: 'creating-tickets', labelKey: 'sections.creatingTickets.supportRequest.title' },
      { id: 'creating-tickets', labelKey: 'sections.creatingTickets.buildManagement.title' },
    ],
  },
  { id: 'ticket-detail', labelKey: 'sections.ticketDetail.title' },
  { id: 'address-information', labelKey: 'sections.addressInformation.title' },
  { id: 'reporting', labelKey: 'sections.reporting.title' },
  { id: 'notifications', labelKey: 'sections.notifications.title' },
  { id: 'profile', labelKey: 'sections.profile.title' },
  { id: 'tips', labelKey: 'sections.tips.title' },
];

// Must list every section id rendered below — the scroll-spy observes exactly these.
const SECTION_IDS = [
  'getting-started',
  'dashboard',
  'ticket-list',
  'creating-tickets',
  'ticket-detail',
  'address-information',
  'reporting',
  'notifications',
  'profile',
  'tips',
];

export function UserGuidePage() {
  const [activeSection, setActiveSection] = useState(SECTION_IDS[0]);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const location = useLocation();

  // IntersectionObserver for active section tracking
  const setupObserver = useCallback(() => {
    observerRef.current?.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
            history.replaceState(null, '', `#${entry.target.id}`);
          }
        }
      },
      {
        // Offset for sticky topbar (64px) + breadcrumbs (~44px) + padding
        rootMargin: '-112px 0px -60% 0px',
      }
    );

    for (const id of SECTION_IDS) {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    }
  }, []);

  useEffect(() => {
    setupObserver();
    return () => observerRef.current?.disconnect();
  }, [setupObserver]);

  // Scroll to hash on mount
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          setActiveSection(id);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex gap-8">
      {/* Sticky TOC sidebar — desktop only */}
      <aside className="hidden lg:block w-56 shrink-0">
        <div className="sticky top-28">
          <TableOfContents items={TOC_ITEMS} activeSection={activeSection} />
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0 space-y-6 pb-16">
        <GettingStartedSection />
        <DashboardSection />
        <TicketListSection />
        <CreatingTicketsSection />
        <TicketDetailSection />
        <AddressInformationSection />
        <ReportingSection />
        <NotificationsSection />
        <ProfileSection />
        <TipsSection />
      </div>

      {/* Mobile TOC */}
      <MobileTocSheet items={TOC_ITEMS} activeSection={activeSection} />
    </div>
  );
}
